import { ApiJobDetailOk, ApiPrevNextJobOk } from '@utils/data-fetching';
import {
  Button, Tooltip,
} from '@components/common';
import { XMarkIcon, Bars3Icon } from '@heroicons/react/24/outline';
import { useTwoColumnLayout } from '@components/layout/Layouts/TwoColumnLayout/TwoColumnLayoutContext';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { NavButton } from '@components/templates/job/detail/partials/NavButton';
import { clsx } from 'clsx';
import { ModalPrevNextJobs } from '@type/job';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';

// Interface
// ************************************
export interface ShareAndNavigationProps {
  job: ApiJobDetailOk | null;
  context?: 'mobile' | 'desktop';
  apiPrevNextJobs?: ApiPrevNextJobOk | null;
  modalPrevNextJobs?: ModalPrevNextJobs;
  closeJobModal?: () => void;
  className?: string;
  modalNavigateToJob?: (nextJobSlug: string, direction: 'prev' | 'next') => void;
  hideNavigation?: boolean;
}

/**
 * @description Share And Navigation. Renders the sharing button, prev & next button and close modal button (if opened in modal).
 * - **job** - ApiJobDetailOk - The job object.
 * - **context** - 'mobile' | 'desktop' - The context where is displayed this component. Default 'desktop'.
 * - **apiPrevNextJobs** - ApiPrevNextJobOk - Prev & next jobs, for in page navigation (not modal).
 * - **modalPrevNextJobs** - ModalPrevNextJobs - Prev & next jobs, for modal navigation. The data comes from SWR.
 * - **closeModal** - function - Function to close the modal, if opened in a modal, optional.
 * - **className** - string - Additional classnames, for styling purposes, optional.
 * - **modalPrevNextJobs** - Prev & next jobs, for modal navigation.
 * - **modalNavigateToJob** - function - The function to use to navigate to prev / next job (in modal).
 */
export const ShareAndNavigation = (props: ShareAndNavigationProps) => {
  const {
    job,
    context = 'desktop',
    apiPrevNextJobs,
    modalPrevNextJobs,
    closeJobModal,
    className,
    modalNavigateToJob,
    hideNavigation,
  } = props;

  // Translation
  const { t } = useTranslation('common');
  const { router } = useExtendedRouter();

  // TwoColumnLayout context for sidebar toggle
  const twoColumnLayout = useTwoColumnLayout();

  // Helpers
  // *****************************************
  const isJobModal = useMemo(
    () => !!closeJobModal,
    [closeJobModal],
  );

  // Job detail page nav tracking classes
  // ************************************
  const pageNavTrackingClasses = 'tr_job_detail_nav_arrows_single_page';

  // Modal nav tracking classes
  // ************************************
  const modalNavTrackingClasses = useMemo(
    () => (context === 'mobile' ? 'tr_job_detail_nav_arrows_mobile' : 'tr_job_detail_nav_arrows_desktop'),
    [context],
  );

  // Button colors, based on the current context.
  // ************************************
  const buttonColor = useMemo(
    () => (context === 'mobile' ? 'white' : 'light'),
    [context],
  );

  // Button classes, based on the current context.
  // ************************************
  const buttonClass = useMemo(
    () => {
      let classes = '!p-2';
      classes += (context === 'mobile' ? ' dark:bg-surface-100 dark:text-ink' : '');
      return classes;
    },
    [context],
  );

  // Render component
  // ************************************
  return (
    <div className={className}>
      <div className="flex items-center gap-x-4">
        {/* Sidebar toggle button — only when inside a job-detail modal, where the
            global MainNavbar (top bar) is covered by the modal overlay. On the
            standalone /mc/job/[slug] page the MainNavbar already provides this
            control on mobile. */}
        {twoColumnLayout && isJobModal && (
          <Button
            color={buttonColor}
            className={clsx(buttonClass, 'lg:hidden')}
            size="sm"
            rounding="full"
            onClick={twoColumnLayout.toggleLeftSidebar}
            aria-label={twoColumnLayout.isLeftOpen ? 'Close sidebar' : 'Open sidebar'}
          >
            <Bars3Icon className="size-5" />
          </Button>
        )}

        {/* Close button - modal only */}
        {isJobModal && (
        <Tooltip
          tooltipText={t('global.btn.close')}
          position="bottom"
          colorScheme="black"
        >
          <Button
            color={buttonColor}
            className={clsx(buttonClass, 'tr-job-close')}
            size="sm"
            rounding="full"
            tabIndex={job ? 0 : -1}
            onClick={closeJobModal}
          >
            <XMarkIcon className="size-5" />
          </Button>
        </Tooltip>
        )}

        {job && (
          <>
            <div className="ml-auto flex items-center gap-x-4">
              {isJobModal ? (
                !hideNavigation && (
                  <>
                    <NavButton
                      direction="prev"
                      disabled={!modalPrevNextJobs?.prevJob}
                      color={buttonColor}
                      className={clsx(buttonClass, modalNavTrackingClasses, 'tr-job-nav-left')}
                      onClick={() => (modalPrevNextJobs?.prevJob?.slug && modalNavigateToJob ? modalNavigateToJob(modalPrevNextJobs?.prevJob.slug, 'prev') : {})}
                    />
                    <NavButton
                      direction="next"
                      disabled={!modalPrevNextJobs?.nextJob}
                      color={buttonColor}
                      className={clsx(buttonClass, modalNavTrackingClasses, 'tr-job-nav-right')}
                      onClick={() => (modalNavigateToJob && modalPrevNextJobs?.nextJob?.slug ? modalNavigateToJob(modalPrevNextJobs?.nextJob.slug, 'next') : {})}
                    />

                  </>
                )
              ) : (
                !hideNavigation && (
                  <>
                    {apiPrevNextJobs?.prevJob && (
                      <NavButton
                        navigationJob={apiPrevNextJobs.prevJob}
                        direction="prev"
                        color={buttonColor}
                        className={clsx(buttonClass, pageNavTrackingClasses)}
                        onClick={() => { router.back(); }}
                      />
                    )}
                    {apiPrevNextJobs?.nextJob && (
                      <NavButton
                        navigationJob={apiPrevNextJobs?.nextJob}
                        direction="next"
                        color={buttonColor}
                        className={clsx(buttonClass, pageNavTrackingClasses)}
                      />
                    )}
                  </>
                )
              )}
            </div>
          </>
        )}
      </div>

    </div>
  );
};
